import { useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// Module-level shared state — a single subscription is enough for the whole app.
let currentUser: User | null = null;
let initialized = false;
const listeners = new Set<(u: User | null) => void>();

function notify(u: User | null) {
  currentUser = u;
  listeners.forEach((cb) => cb(u));
}

function ensureSubscribed() {
  if (initialized) return;
  initialized = true;
  // Hydrate from existing session immediately.
  supabase.auth.getSession().then(({ data }) => {
    notify(data.session?.user ?? null);
  });
  supabase.auth.onAuthStateChange((_event, session) => {
    notify(session?.user ?? null);
  });
}

/**
 * Single shared auth subscription. Replaces per-component
 * `supabase.auth.onAuthStateChange` calls so we don't open
 * one channel per mounted component.
 */
export function useAuthUser(): User | null {
  const [user, setUser] = useState<User | null>(currentUser);

  useEffect(() => {
    ensureSubscribed();
    listeners.add(setUser);
    // Sync if state changed between render and effect.
    if (currentUser !== user) setUser(currentUser);
    return () => {
      listeners.delete(setUser);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return user;
}
